// TasksConfig.tsx (updated with checkboxes)
import React, { useState, useEffect } from 'react';
import { useConfigStore } from '@/store/configStore';
import { Edit2, Trash2 } from 'lucide-react';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { Button } from '@/components/common/Button';
import { Plus, X } from 'lucide-react';

export const TasksConfig: React.FC<{ onSetActionCallback?: (callbacks: { add?: () => void }) => void }> = ({ onSetActionCallback }) => {
  const { 
    taskTemplates,
    loading, 
    fetchTaskTemplates, 
    createTaskTemplate, 
    updateTaskTemplate, 
    deleteTaskTemplate 
  } = useConfigStore();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    subTasks: [''],
    defaultPriority: 'Medium',
    defaultEstimatedTime: 60
  });

  useEffect(() => {
    fetchTaskTemplates();
  }, []);

  useEffect(() => {
    onSetActionCallback?.({
      add: () => setShowAddModal(true)
    });
  }, [onSetActionCallback]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedTasks(templates.map(template => template._id));
    } else {
      setSelectedTasks([]);
    }
  };

  const handleSelectTask = (taskId: string) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSubmit = async () => {
    try {
      const filteredSubTasks = formData.subTasks.filter(st => st.trim() !== '');
      if (filteredSubTasks.length === 0) {
        return;
      }
      
      if (editingTemplate) {
        await updateTaskTemplate(editingTemplate._id, { ...formData, subTasks: filteredSubTasks });
      } else {
        await createTaskTemplate({ ...formData, subTasks: filteredSubTasks });
      }
      
      await fetchTaskTemplates();
      handleClose();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    }
  };

  const handleClose = () => {
    setShowAddModal(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      subTasks: [''],
      defaultPriority: 'Medium',
      defaultEstimatedTime: 60
    });
  };

  const handleEdit = (template: any) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subTasks: template.subTasks && template.subTasks.length > 0 ? [...template.subTasks] : [''],
      defaultPriority: template.defaultPriority || 'Medium',
      defaultEstimatedTime: template.defaultEstimatedTime || 60
    });
    setShowAddModal(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      deleteTaskTemplate(id);
    }
  };

  const addSubTask = () => {
    setFormData({ ...formData, subTasks: [...formData.subTasks, ''] });
  };

  const updateSubTask = (index: number, value: string) => {
    const newSubTasks = [...formData.subTasks];
    newSubTasks[index] = value;
    setFormData({ ...formData, subTasks: newSubTasks });
  };

  const removeSubTask = (index: number) => {
    const newSubTasks = formData.subTasks.filter((_, i) => i !== index);
    setFormData({ ...formData, subTasks: newSubTasks.length > 0 ? newSubTasks : [''] });
  };

  const templates = Array.isArray(taskTemplates) ? taskTemplates : [];

  // Calculate total subtasks
  const totalSubtasks = templates.reduce((acc, template) => 
    acc + (template.subTasks?.length || 0), 0
  );

  return (
    <div className="h-full bg-white mx-0 my-0">
      <div className="h-full flex flex-col">
        <div className="overflow-auto flex-1">
          <table className="w-full border border-gray-200 border-collapse">
            <thead>
              <tr className="border border-gray-200">
                <th className="text-left bg-gray-100 px-4 border py-1 w-10">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300"
                    onChange={handleSelectAll}
                    checked={selectedTasks.length === templates.length && templates.length > 0}
                  />
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  TASK
                </th>
                <th className="text-right bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  TOTAL SUBTASKS
                </th>
                <th className="w-20 bg-gray-100 border"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-500 border">
                    Loading task templates...
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-500 border">
                    No task templates found
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr key={template._id} className="border border-gray-100 hover:bg-gray-50">
                    <td className="px-4 border py-1">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300"
                        checked={selectedTasks.includes(template._id)}
                        onChange={() => handleSelectTask(template._id)}
                      />
                    </td>
                    <td className="px-4 border py-1 text-sm font-medium text-gray-900">
                      {template.name}
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600 text-right">
                      {template.subTasks?.length || 0}
                    </td>
                    <td className="px-4 border py-1">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          onClick={() => handleEdit(template)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          onClick={() => handleDelete(template._id, template.name)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 border py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600 uppercase">
            COUNT <span className="ml-4 font-medium">{templates.length}</span>
          </div>
          <div className="text-sm text-gray-600 uppercase">
            <span className="ml-4 font-medium">{totalSubtasks}</span>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={handleClose}
        title={editingTemplate ? 'Edit Task Template' : 'Add Task Template'}
        size="lg"
      >
        {/* Modal content remains the same */}
        <div className="space-y-4">
          <Input
            label="Template Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter template name"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Priority
            </label>
            <Select
              value={formData.defaultPriority}
              onChange={(e) => setFormData({ ...formData, defaultPriority: e.target.value })}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Time (minutes)
            </label>
            <Input
              type="number"
              value={formData.defaultEstimatedTime}
              onChange={(e) => setFormData({ ...formData, defaultEstimatedTime: parseInt(e.target.value) || 60 })}
              min={1}
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Sub-tasks
              </label>
              <Button
                size="sm"
                variant="secondary"
                onClick={addSubTask}
                icon={<Plus size={14} />}
              >
                Add Sub-task
              </Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {formData.subTasks.map((subTask, index) => (
                <div key={index} className="flex space-x-2">
                  <Input
                    value={subTask}
                    onChange={(e) => updateSubTask(index, e.target.value)}
                    placeholder="Enter sub-task name"
                  />
                  {formData.subTasks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSubTask(index)}
                      className="p-2 text-red-400 hover:text-red-600"
                    >
                      <X size={20} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!formData.name || formData.subTasks.every(st => !st.trim())}
            >
              {editingTemplate ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};